import React, { useState, useEffect } from 'react';
import CardSelector from './CardSelector';
import { POSITIONS } from '../constants';
import { useAppContext } from '../contexts/AppContext';
import { useI18n } from '../hooks/useI18n';

const SetupModal = ({ 
  gameInfo, 
  setGameInfo, 
  heroInfo, 
  setHeroInfo, 
  onComplete,
  playerPositions
}) => {
  const { appName } = useAppContext();
  const { t, noTranslate } = useI18n();
  const [showCardSelector, setShowCardSelector] = useState(false);

  useEffect(() => {
    // Load saved stakes from localStorage on component mount
    const savedStakes = localStorage.getItem('pokerStakes');
    if (savedStakes) {
      const [sb, bb] = savedStakes.split('/');
      setGameInfo(prev => ({
        ...prev,
        stakes: `${sb}/${bb}`,
        smallBlind: parseInt(sb),
        bigBlind: parseInt(bb)
      }));
    }
  }, []);

  useEffect(() => {
    const bigBlind = parseInt(gameInfo.stakes.split('/')[1]);
    setGameInfo(prev => ({
      ...prev,
      effectiveStack: bigBlind * 100 // 100 big blinds
    }));
  }, [gameInfo.stakes]);

  const handleGameInfoChange = (e) => {
    let value = e.target.value;
    let name = e.target.name;
    
    if (name === 'effectiveStack') {
      value = Math.max(0, parseInt(value) || 0);
      setGameInfo({ ...gameInfo, [name]: value });
    } else if (name === 'smallBlind' || name === 'bigBlind') {
      value = Math.max(0, parseInt(value) || 0);
      const updatedGameInfo = { ...gameInfo, [name]: value };
      
      // Update stakes string when either blind changes
      if (name === 'smallBlind') {
        updatedGameInfo.stakes = `${value}/${gameInfo.bigBlind || 0}`;
      } else {
        updatedGameInfo.stakes = `${gameInfo.smallBlind || 0}/${value}`;
      }
      
      setGameInfo(updatedGameInfo);
    } else {
      setGameInfo({ ...gameInfo, [name]: value });
    }
  };

  const handleHeroInfoChange = (e) => {
    setHeroInfo({ ...heroInfo, [e.target.name]: e.target.value });
  };

  const handleHeroCards = (cards) => {
    setHeroInfo(prev => ({ ...prev, hand: cards }));
    setShowCardSelector(false);
  };

  const handleComplete = () => {
    if (!heroInfo.position) {
      alert(t('messages.selectPositionFirst'));
      return;
    }
    
    if (heroInfo.hand[0] === '' || heroInfo.hand[1] === '') {
      alert(t('messages.selectHandFirst'));
      return;
    }

    if (!gameInfo.effectiveStack || gameInfo.effectiveStack <= 0) {
      alert(t('messages.enterValidStack'));
      return;
    }

    // Save stakes to localStorage before completing
    localStorage.setItem('pokerStakes', gameInfo.stakes);
    
    onComplete();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-lg max-w-lg w-full">
        <div className="bg-blue-600 text-white font-semibold px-4 py-3">
          <h2 className="text-xl">{t('setup.title', { appName: noTranslate(appName) })}</h2>
        </div>
        
        <div className="p-6">
          <div className="mb-6">
            <h3 className="text-lg font-medium text-gray-800 mb-3">{t('setup.gameInfo')}</h3>
            <div className="space-y-3">
              <div className="flex space-x-4">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('setup.smallBlind')}</label>
                  <input
                    type="number"
                    name="smallBlind"
                    value={gameInfo.smallBlind || ''}
                    onChange={handleGameInfoChange}
                    min="0"
                    placeholder={t('setup.enterSmallBlind')}
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('setup.bigBlind')}</label>
                  <input
                    type="number"
                    name="bigBlind"
                    value={gameInfo.bigBlind || ''}
                    onChange={handleGameInfoChange}
                    min="0"
                    placeholder={t('setup.enterBigBlind')}
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('setup.effectiveStack')}</label>
                <input
                  type="number"
                  name="effectiveStack"
                  value={gameInfo.effectiveStack || ''}
                  onChange={handleGameInfoChange}
                  min="0"
                  placeholder={t('setup.enterEffectiveStack')}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('poker.playerCount')}</label>
                <select
                  name="players"
                  value={gameInfo.players}
                  onChange={handleGameInfoChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {[6, 7, 8, 9].map(num => (
                    <option key={num} value={num}>{num}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
          
          <div className="mb-6">
            <h3 className="text-lg font-medium text-gray-800 mb-3">{t('setup.heroInfo')}</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('setup.position')}</label>
                <select
                  name="position"
                  value={heroInfo.position}
                  onChange={handleHeroInfoChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">{t('poker.selectPosition')}</option>
                  {playerPositions.map(pos => (
                    <option key={pos} value={pos}>{noTranslate(pos)}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('setup.heroHand')}</label>
                <button
                  onClick={() => setShowCardSelector(true)}
                  className="w-full p-2 bg-blue-500 hover:bg-blue-600 text-white font-medium rounded-md transition duration-200"
                >
                  {t('poker.selectHand')}
                </button>
                
                {showCardSelector && (
                  <div className="mt-2">
                    <CardSelector 
                      onSelectCards={handleHeroCards} 
                      numCards={2} 
                    />
                  </div>
                )}
                
                {heroInfo.hand.length === 2 && heroInfo.hand[0] !== '' && heroInfo.hand[1] !== '' && (
                  <div className="flex mt-2 justify-center">
                    {heroInfo.hand.map((card, index) => (
                      <div
                        key={index}
                        className="m-1 rounded shadow"
                        style={{
                          backgroundImage: `url(/images/cards/${card}.svg)`,
                          backgroundPosition: 'center',
                          width: '42px',
                          height: '58px',
                        }}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
          
          <div className="flex justify-end">
            <button
              onClick={handleComplete}
              className="bg-green-500 hover:bg-green-600 text-white font-semibold py-2 px-6 rounded-md transition duration-200"
            >
              {t('poker.startHand')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SetupModal;